/**
 * Import pictures from a folder.
 *
 * The photographs arrive as an export: one folder, one file per record, each named after
 * the record it belongs to — "S4CA-236.jpg". That name is the only thing that says what is
 * in the picture, so the whole of this page is one rule read out of filenames, and the
 * Worker is what reads it.
 *
 * "Import Pictures", not "Import Partner Pictures": the code in a filename is matched
 * against the whole registry, so a training school's photograph and a project's file
 * themselves here exactly as a partner unit's does. Nothing in the matching ever knew the
 * difference — it is `partners.s4ca_code` either way — so the page needed the honest name
 * rather than new code. The browser sends the names, gets back a verdict for each,
 * shows them, and then sends the files it was told to send. Nothing it works out for
 * itself decides whose record a photograph lands on.
 *
 * What it does to the archive is not additive: a unit that already has a picture has it
 * written over, and the old one is deleted rather than kept beside the new one. That is
 * the point of the import — these are corrections to the portraits on file — but it is
 * also why the dry run comes first and says how many of them there are, and why the page
 * is for owners and administrators only.
 *
 * Files are sent one at a time. A few hundred photographs do not fit in one request, and
 * a single request that failed half way through would leave nobody able to say which of
 * them had landed. One file per request means the progress bar is honest, a run can be
 * stopped, and an interrupted run can simply be run again.
 */
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, reportClientError } from '../lib/api'
import { HEIC_ACCEPT } from '../lib/heic'
import { shrinkInBrowser } from '../components/ImageUpload'
import {
  Card, Tile, Badge, Notice, Spinner, useToast, fmtBytes, fmtInt,
} from '../components/ui'
import { Dropzone } from '../components/Dropzone'

/**
 * What the Worker's verdicts are called on screen, and how loudly.
 *
 * `no_partner` is the one in red. A file with no code in its name is a file somebody
 * forgot to rename; a well-formed code with no record behind it is either a record
 * missing from the archive or a photograph filed against the wrong one, and both of
 * those are worth stopping over.
 */
const STATUS = {
  ready: { label: 'Will import', tone: 'ok' },
  no_partner: { label: 'No such record', tone: 'danger' },
  no_code: { label: 'No S4CA code in the name', tone: 'warn' },
  no_number: { label: 'S4CA code with no numbers', tone: 'warn' },
  ambiguous: { label: 'Two codes in one name', tone: 'warn' },
  duplicate: { label: 'Two files, one record', tone: 'warn' },
  not_image: { label: 'Not a picture', tone: undefined },
}

/** The order the plan is listed in: what will happen first, what was refused after. */
const STATUS_ORDER = ['ready', 'no_partner', 'duplicate', 'ambiguous', 'no_number', 'no_code', 'not_image']

/**
 * Housekeeping a folder carries that nobody chose — .DS_Store and its like. Dropped
 * here as well as in the Worker, and identically, so the plan the Worker returns lines
 * up one for one with the files this page is holding.
 */
const chosen = (file) => {
  const name = (file?.name ?? '').trim()
  return !!name && !name.startsWith('.')
}

/**
 * Every file under a dropped folder, however deep.
 *
 * A folder cannot be read from `dataTransfer.files` — that is empty for a directory — so
 * the drop is walked through the entries API instead. `webkitGetAsEntry` has to be called
 * before the first await, while the drop event's data is still alive, which is why the
 * entries are collected in one pass at the top.
 */
async function filesFromDrop(dataTransfer) {
  const entries = [...(dataTransfer.items || [])].map((i) => i.webkitGetAsEntry?.()).filter(Boolean)
  if (!entries.length) return [...(dataTransfer.files || [])]

  const out = []
  const walk = async (entry) => {
    if (entry.isFile) {
      out.push(await new Promise((resolve, reject) => entry.file(resolve, reject)))
      return
    }
    const reader = entry.createReader()
    // readEntries hands back one batch at a time; an empty batch is the end of the folder.
    for (;;) {
      const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject))
      if (!batch.length) break
      for (const e of batch) await walk(e)
    }
  }
  for (const entry of entries) await walk(entry)
  return out
}

export default function SettingsPhotoImport({ user }) {
  const toast = useToast()
  const qc = useQueryClient()
  // Set while a run is going, and read inside the loop: the Stop button has to reach
  // code that is already running, which state would not do until the next render.
  const stopped = useRef(false)

  const [files, setFiles] = useState([])
  const [plan, setPlan] = useState(null)
  const [planning, setPlanning] = useState(false)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(null)
  const [outcome, setOutcome] = useState(null)
  // What actually became of each planned row, by its index in the plan, filled in as the
  // run goes. Without it the table would still be promising "will import" about pictures
  // that have already gone up — or failed.
  const [results, setResults] = useState({})

  if (!user.caps.manageSettings) {
    return (
      <PageHeader title="Import Pictures">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Importing a folder of pictures writes over the photograph on every record it
          matches, so it is kept to owners and administrators. You are signed in as{' '}
          <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const maxBytes = user.maxImageBytes ?? 307200
  const running = !!progress

  const reset = () => {
    setFiles([])
    setPlan(null)
    setOutcome(null)
    setResults({})
    setError(null)
  }

  /** Take a folder and ask the Worker what it would do with it. */
  const choose = async (picked) => {
    const list = [...picked].filter(chosen)
    setOutcome(null)
    setResults({})
    setError(null)
    setPlan(null)
    setFiles(list)
    if (!list.length) {
      setError('That folder holds no files to import.')
      return
    }
    setPlanning(true)
    try {
      setPlan(await api('media/import/plan', { method: 'POST', body: { files: list.map((f) => f.name) } }))
    } catch (err) {
      setError(err.message)
    } finally {
      setPlanning(false)
    }
  }

  /**
   * Send every picture the plan marked ready, one after another.
   *
   * The plan comes back in the order the names went out, so row `i` belongs to file `i`.
   * Even if that ever slipped, it could not put a photograph on the wrong record — the
   * Worker reads the code out of the file it is actually handed and files it by that.
   */
  const run = async () => {
    const ready = plan.rows
      .map((row, index) => ({ row, index, file: files[index] }))
      .filter(({ row, file }) => row.status === 'ready' && file)
    if (!ready.length) return

    stopped.current = false
    setOutcome(null)
    setResults({})
    setProgress({ done: 0, total: ready.length, name: ready[0].row.name })

    const failures = []
    let imported = 0
    let replaced = 0

    for (const [i, { row, index, file }] of ready.entries()) {
      if (stopped.current) break
      setProgress({ done: i, total: ready.length, name: row.name })
      try {
        // Shrunk here rather than on arrival, as an ordinary upload is: several hundred
        // full-size photographs would otherwise be several hundred oversized requests.
        // The shrunk copy keeps the filename it came with, code and all.
        const prepared = await shrinkInBrowser(file, maxBytes)
        const fd = new FormData()
        fd.append('file', prepared.file)
        const res = await api('media/import', { method: 'POST', formData: fd })
        imported++
        if (res.replaced) replaced++
        setResults((prev) => ({ ...prev, [index]: { ok: true, replaced: !!res.replaced } }))
      } catch (err) {
        failures.push({ name: file.name, message: err.message })
        setResults((prev) => ({ ...prev, [index]: { ok: false, message: err.message } }))
        reportClientError('photo-import', err, {
          filename: file.name,
          mime_type: file.type,
          bytes: file.size,
          entity: 'partner',
        })
      }
    }

    setProgress(null)

    // One line in the history for the whole run, written from the filenames rather than
    // from anything this page concluded — see the report route.
    let report = null
    try {
      report = await api('media/import/report', {
        method: 'POST',
        body: { files: files.map((f) => f.name), imported, failed: failures.length },
      })
    } catch {
      // The pictures are in. Failing to write the summary line is not worth an error on
      // top of a finished import; the per-picture entries are in the log either way.
    }

    setOutcome({
      imported,
      replaced,
      failures,
      stopped: stopped.current,
      messages: report?.messages ?? plan.messages,
    })
    // Photographs are on screen all over the archive, and every one of them has just
    // been replaced under a new media id.
    qc.invalidateQueries()
    toast.ok(
      failures.length
        ? `${fmtInt(imported)} pictures imported, ${failures.length} failed`
        : `${fmtInt(imported)} picture${imported === 1 ? '' : 's'} imported`
    )
  }

  const s = plan?.summary
  // The index is carried through the sort because it is how a row finds its result: the
  // plan came back in the order the filenames went out, and that is the order `files` and
  // `results` are keyed by.
  const sorted = (plan?.rows ?? [])
    .map((row, index) => ({ ...row, index }))
    .sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        (a.code ?? '').localeCompare(b.code ?? '') ||
        a.name.localeCompare(b.name)
    )

  // The Worker puts the record it could not find first (see photoImportPlan), because
  // that is the one worth a red box of its own. The rest are one notice between them —
  // three boxes with the same heading read as three problems.
  const setAside = plan ? plan.messages.slice(s.no_partner ? 1 : 0) : []

  return (
    <PageHeader
      title="Import Pictures"
      sub="A folder of photographs, filed by the S4CA code in each filename"
      actions={
        plan && !running ? (
          <button className="btn" onClick={reset}>
            Choose another folder
          </button>
        ) : null
      }
    >
      <div className="col">
        <Notice icon="info" title="How a folder is read">
          <p>
            Every file is filed by the <strong>S4CA-000</strong> code in its name — anywhere in
            the name, so “S4CA-236.jpg” and “Team S4CA-236 2024.jpeg” are the same record.
            A name whose code has no three numbers in it, or none at all, is discarded.
          </p>
          <p style={{ marginTop: 8 }}>
            The code is matched against the <strong>whole registry</strong>, so one folder can
            carry pictures for <strong>partner units, training schools and projects</strong>{' '}
            together. Nothing here needs to know which is which, and nothing needs sorting
            beforehand — the code in the filename is the only thing that decides.
          </p>
          <p style={{ marginTop: 8 }}>
            A picture that matches a record <strong>replaces</strong> the photograph on that
            record. The old picture is deleted, not archived. Nothing is written until you have
            read the plan below and pressed Import.
          </p>
        </Notice>

        {!plan && (
          <Dropzone
            onFiles={choose}
            readDrop={filesFromDrop}
            accept={`image/jpeg,image/png,image/webp,image/gif,image/avif,${HEIC_ACCEPT}`}
            directory
            busy={planning}
            busyLabel={`Reading ${fmtInt(files.length)} files…`}
            icon="folder"
            title="Choose the import folder"
          >
            Drop the folder here, or click to pick it. Sub-folders are read too, and anything
            larger than {Math.round(maxBytes / 1024)} KB is resized before it is stored.
          </Dropzone>
        )}

        {error && (
          <Notice tone="danger" icon="warning" title="That folder could not be read">
            {error}
          </Notice>
        )}

        {s && (
          <>
            <div className="grid grid-tiles">
              <Tile label="Pictures to import" value={fmtInt(s.ready)} tone={s.ready ? 'ok' : undefined} />
              <Tile
                label="Replacing a picture"
                value={fmtInt(s.replacing)}
                note={s.replacing ? 'the old ones are deleted' : 'none of these units has one yet'}
              />
              <Tile
                label="No matching record"
                value={fmtInt(s.no_partner)}
                tone={s.no_partner ? 'danger' : undefined}
                note={s.missing_codes.slice(0, 4).join(', ') || null}
              />
              <Tile
                label="Discarded"
                value={fmtInt(s.discarded + s.duplicate + s.not_image)}
                note="no usable code, or two files for one unit"
              />
            </div>

            {s.no_partner > 0 && (
              <Notice tone="danger" icon="warning" title="Some pictures name a record that is not here">
                {plan.messages[0]}
              </Notice>
            )}

            {setAside.length > 0 && (
              <Notice tone="warn" icon="warning" title="Some files were set aside">
                {setAside.length === 1 ? (
                  setAside[0]
                ) : (
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {setAside.map((msg) => (
                      <li key={msg}>{msg}</li>
                    ))}
                  </ul>
                )}
              </Notice>
            )}

            <Card
              title={`${fmtInt(s.files)} Files in the Folder`}
              sub={outcome ? 'What the import did with each of them' : 'What the import will do with each of them'}
              actions={
                running ? (
                  <button className="btn" onClick={() => (stopped.current = true)}>
                    Stop
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={run} disabled={!s.ready || !!outcome}>
                    {outcome
                      ? 'Imported'
                      : s.ready
                        ? `Import ${fmtInt(s.ready)} picture${s.ready === 1 ? '' : 's'}`
                        : 'Nothing to import'}
                  </button>
                )
              }
            >
              {running && (
                <div className="col" style={{ marginBottom: 12 }}>
                  <div className="row">
                    <Spinner />
                    <span className="small">
                      {fmtInt(progress.done + 1)} of {fmtInt(progress.total)} — {progress.name}
                    </span>
                  </div>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="table-wrap">
                <table className="data compact">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Code</th>
                      <th>Record</th>
                      <th>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r) => (
                      <tr key={r.index}>
                        <td className="small truncate" title={r.name}>
                          {r.name}
                        </td>
                        <td className="small">{r.code ?? '—'}</td>
                        <td className="small">
                          {r.partner_name ? (
                            <>
                              {r.partner_name}
                              {r.partner_status && r.partner_status !== 'active' && (
                                <>
                                  {' '}
                                  <Badge>{r.partner_status}</Badge>
                                </>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        {/* Once a row has been through the run it reports what happened to
                            it, rather than going on promising what was going to. */}
                        <td className="small">
                          {results[r.index] ? (
                            <>
                              <Badge tone={results[r.index].ok ? 'ok' : 'danger'}>
                                {results[r.index].ok ? 'Imported' : 'Failed'}
                              </Badge>
                              <span className="subtle">
                                {results[r.index].ok
                                  ? results[r.index].replaced
                                    ? ' the old picture was deleted'
                                    : ''
                                  : ` ${results[r.index].message}`}
                              </span>
                            </>
                          ) : (
                            <>
                              <Badge tone={STATUS[r.status]?.tone}>
                                {STATUS[r.status]?.label ?? r.status}
                              </Badge>
                              {r.status === 'ready' && r.replaces && (
                                <span className="subtle"> replaces a picture</span>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {outcome && (
          <Notice
            tone={outcome.failures.length ? 'warn' : 'ok'}
            icon={outcome.failures.length ? 'warning' : 'check'}
            title={
              outcome.stopped
                ? 'Import stopped part way through'
                : `${fmtInt(outcome.imported)} picture${outcome.imported === 1 ? '' : 's'} imported`
            }
          >
            <p>
              {fmtInt(outcome.replaced)} of them replaced a photograph that was already on the
              record; those old pictures have been deleted.
              {outcome.stopped
                ? ' The pictures that had already gone up are in place — run the folder again to finish the rest.'
                : ''}
            </p>
            {outcome.failures.length > 0 && (
              <ul style={{ margin: '8px 0 0 18px' }}>
                {outcome.failures.map((f) => (
                  <li key={f.name} className="small">
                    <strong>{f.name}</strong> — {f.message}
                  </li>
                ))}
              </ul>
            )}
          </Notice>
        )}

        {files.length > 0 && !plan && !planning && !error && (
          <p className="small subtle">
            {fmtInt(files.length)} files, {fmtBytes(files.reduce((a, f) => a + f.size, 0))} in total.
          </p>
        )}
      </div>
    </PageHeader>
  )
}
